import React, { useState } from 'react';
import ConnectionStatusRow from './ConnectionStatusRow';
import CountsRow from './CountsRow';
import LookupRow from './LookupRow';
import TaxGroupRow from './TaxGroupRow';
import DateRangePicker from './components/DateRangePicker';
import ActionLogModal from './components/ActionLogModal';
import { DevTabProps, DateRange } from './types';
import { getLogStats } from './utils/devTabLogger';

const DevTabTable: React.FC<DevTabProps> = ({ qboConnectionStatus, qboTokenInfo, qboConnectionData, items }) => {
  // Initialize with Year-to-Date default
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: startOfYear.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0]
  });

  // State for managing search IDs across rows
  const [searchIds, setSearchIds] = useState<{
    customer: string;
    invoice: string;
    payment: string;
  }>({
    customer: '',
    invoice: '',
    payment: ''
  });
  
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [logStats, setLogStats] = useState(getLogStats());

  // Crawl filter options for customer row
  const [crawlFilters, setCrawlFilters] = useState({
    ignoreArchivedInvoices: false,  // When true, archived invoices don't count as mismatches
    ignoreUnsentInvoices: false,    // When true, unsent invoices (status < 33) don't count as mismatches
  });

  // Handler for when an ID is clicked from associated data
  const handleIdClick = (id: string, targetType: 'invoice' | 'payment' | 'customer') => {
    setSearchIds(prev => ({
      ...prev,
      [targetType]: id
    }));
  };
  
  // Listen for log updates to refresh the counter
  React.useEffect(() => {
    const handleLogUpdate = () => {
      setLogStats(getLogStats());
    };
    
    window.addEventListener('devtab-log-updated', handleLogUpdate);
    
    return () => {
      window.removeEventListener('devtab-log-updated', handleLogUpdate);
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-4">
            <DateRangePicker 
              dateRange={dateRange} 
              onDateRangeChange={setDateRange} 
            />
            
            {/* Action Log Button */}
            <button
              onClick={() => setIsLogModalOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-indigo-600 text-indigo-600 text-xs rounded hover:bg-indigo-50 transition-colors whitespace-nowrap"
              title="View action log"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Log
              {logStats.total > 0 && (
                <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {logStats.total}
                </span>
              )}
            </button>
          </div>
          
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-32">Step</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-36">Action</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-64">EServ</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-64">QBO</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-48">Details</th>
                <th className="border border-gray-300 px-2 py-2 text-center font-medium w-32">Status</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-48">Associated</th>
                <th className="border border-gray-300 px-3 py-2 text-left font-medium w-64">Issues</th>
              </tr>
            </thead>
            <tbody>
              <ConnectionStatusRow 
                qboConnectionStatus={qboConnectionStatus} 
                qboTokenInfo={qboTokenInfo}
                qboConnectionData={qboConnectionData}
              />
              <CountsRow dateRange={dateRange} />
              <LookupRow 
                type="customer" 
                stepNumber={3} 
                items={items} 
                onIdClick={handleIdClick}
                externalSearchId={searchIds.customer}
                onSearchIdChange={(id) => setSearchIds(prev => ({ ...prev, customer: id }))}
                crawlFilters={crawlFilters}
                onCrawlFiltersChange={setCrawlFilters}
              />
              <LookupRow 
                type="invoice" 
                stepNumber={4} 
                items={items} 
                onIdClick={handleIdClick}
                externalSearchId={searchIds.invoice}
                onSearchIdChange={(id) => setSearchIds(prev => ({ ...prev, invoice: id }))}
                crawlFilters={crawlFilters}
                onCrawlFiltersChange={setCrawlFilters}
              />
              <LookupRow 
                type="payment" 
                stepNumber={5} 
                items={items} 
                onIdClick={handleIdClick}
                externalSearchId={searchIds.payment}
                onSearchIdChange={(id) => setSearchIds(prev => ({ ...prev, payment: id }))}
                crawlFilters={crawlFilters}
                onCrawlFiltersChange={setCrawlFilters}
              />
              <TaxGroupRow stepNumber={6} />
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Action Log Modal */}
      <ActionLogModal 
        isOpen={isLogModalOpen} 
        onClose={() => setIsLogModalOpen(false)} 
      />
    </div>
  );
};

export default DevTabTable;
